import { useMemo } from "react";
import { Video, Image as ImageIcon, Loader2 } from "lucide-react";
import { DAY_KEYS, STATUS } from "@/lib/api";
import { PlatformRow, ThumbImg } from "@/components/Bits";
import { useDrag, dragProps } from "@/lib/dragMove";

const pad = (n) => String(n).padStart(2, "0");
const FMT = { video: "Video", photo: "Foto", carousel: "Karussell" };
export const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function Thumb({ post, file }) {
  const Icon = post.content_type === "video" ? Video : ImageIcon;
  return (
    <div className="relative aspect-[4/5] rounded-md overflow-hidden bg-white/5 border border-white/5">
      {file ? <ThumbImg file={file} className="h-full w-full object-cover" />
        : <div className="h-full w-full grid place-items-center text-slate-600"><Icon className="h-5 w-5" /></div>}
      <span className={`absolute top-1 left-1 h-5 w-5 grid place-items-center rounded ${post.content_type === "video" ? "bg-teal-500" : "bg-amber-500"} text-slate-950`}><Icon className="h-3 w-3" /></span>
      {post.ai_pending && <span className="absolute inset-0 grid place-items-center bg-black/50"><Loader2 className="h-4 w-4 animate-spin text-purple-300" /></span>}
      <span className={`absolute bottom-0 inset-x-0 h-1 ${STATUS[post.status]?.cls.split(" ")[0]}`} />
    </div>
  );
}

export default function MonthGrid({ cursor, posts, files = [], rhythm = [], onDayClick, onPostClick, compact }) {
  const filesById = useMemo(() => Object.fromEntries(files.map((f) => [f.id, f])), [files]);
  const rhythmByDay = Object.fromEntries(rhythm.map((r) => [r.day, r]));
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const lead = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [...Array(lead).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => new Date(cursor.getFullYear(), cursor.getMonth(), i + 1))];
  const today = ymd(new Date());
  const ctx = useDrag();
  const dragging = !!ctx?.drag;

  return (
    <div data-testid="month-grid" data-drag-zone="">
      <div className="grid grid-cols-7 gap-2 mb-2">
        {DAY_KEYS.map((d) => (
          <div key={d} className={`px-2 py-1 font-mono text-[11px] uppercase tracking-widest ${rhythmByDay[d] ? "text-teal-300" : "text-slate-500"}`}>
            {d}{rhythmByDay[d] && <span className="ml-1 normal-case tracking-normal text-[10px] text-slate-400">· {FMT[rhythmByDay[d].format]}</span>}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const key = ymd(d);
          const r = rhythmByDay[DAY_KEYS[(d.getDay() + 6) % 7]];
          const dayPosts = posts.filter((p) => p.scheduled_at?.slice(0, 10) === key);
          const past = key < today;
          const isOver = ctx?.over === key;
          return (
            <div key={key} data-testid="calendar-day-cell" onClick={() => onDayClick?.(key)} {...(past ? {} : { "data-drop-date": key })}
              className={`rounded-lg border p-2 transition-colors ${compact ? "min-h-[92px]" : "min-h-[150px]"} ${onDayClick ? "cursor-pointer hover:border-teal-500/40" : ""} ${isOver ? "border-teal-300 bg-teal-400/25 ring-2 ring-teal-300" : r ? "border-teal-500/20 bg-teal-500/[0.04]" : "border-border bg-white/[0.01]"} ${dragging && !past ? "bg-[#3a3f47]" : ""} ${past ? "opacity-60" : ""}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className={`text-xs h-6 w-6 grid place-items-center rounded-full ${key === today ? "bg-teal-400 text-slate-950 font-bold" : "text-slate-400"}`}>{d.getDate()}</span>
                {r && !past && dayPosts.length === 0 && <span className="text-[10px] text-teal-400/70" data-testid={`rhythm-slot-${key}`}>{FMT[r.format]} fällig</span>}
              </div>
              <div className={`grid gap-1.5 ${compact && dayPosts.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                {dayPosts.map((p) => (
                  <button key={p.id} onClick={(e) => { e.stopPropagation(); onPostClick?.(p); }} {...dragProps(ctx, p, filesById[p.media_ids?.[0]])}
                    className={`text-left group select-none cursor-grab active:cursor-grabbing transition-opacity ${ctx?.drag?.post.id === p.id ? "opacity-30" : ""}`} data-testid={`calendar-post-${p.id}`} title={p.title}>
                    <Thumb post={p} file={filesById[p.media_ids?.[0]]} />
                    {!compact && (
                      <div className="mt-1">
                        <p className="text-[11px] leading-tight line-clamp-2 group-hover:text-teal-300">{p.title || "Post"}</p>
                        <div className="flex items-center justify-between mt-1"><PlatformRow platforms={p.platforms.slice(0, 3)} /><span className="text-[10px] text-slate-500">{p.scheduled_at.slice(11, 16)}</span></div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
